import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { ArrowRight, TrendingUp, Shield, Zap, Award } from 'lucide-react';
import { stats } from '../mockData';
import { useLanguage } from '../context/LanguageContext';

const Hero = () => {
  const { t } = useLanguage();
  const [animatedStats, setAnimatedStats] = useState(
    stats.map(stat => ({ ...stat, current: 0 }))
  );

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    stats.forEach((stat, index) => {
      let step = 0;
      const increment = stat.value / steps;
      const timer = setInterval(() => {
        step++;
        setAnimatedStats(prev => {
          const newStats = [...prev];
          newStats[index].current = Math.min(stat.value, increment * step);
          return newStats;
        });

        if (step >= steps) {
          clearInterval(timer);
        }
      }, interval);
    });
  }, []);

  return (
    <section className="hero-section" style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      background: 'var(--bg-primary)',
      position: 'relative',
      overflow: 'hidden',
      paddingTop: '80px'
    }}>
      {/* Hero Background Image - More Visible */}
      <div className="hero-bg-image" style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: 'url(/hero-trading.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        zIndex: 0
      }} />

      {/* Gradient Overlay - Adjusted for better image visibility */}
      <div className="hero-overlay" style={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(135deg, rgba(11, 11, 11, 0.85) 0%, rgba(11, 11, 11, 0.6) 50%, rgba(11, 11, 11, 0.75) 100%)',
        zIndex: 1
      }} />

      {/* Gold Accent Glow */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '5%',
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(212, 175, 55, 0.2) 0%, transparent 60%)',
        borderRadius: '50%',
        filter: 'blur(60px)',
        pointerEvents: 'none',
        zIndex: 1
      }} />

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '40px 20px',
        width: '100%',
        zIndex: 2,
        position: 'relative'
      }}>
        <div className="hero-grid" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '48px',
          alignItems: 'center'
        }}>
          {/* Hero Content - Left Side */}
          <div className="hero-content">
            {/* Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(212, 175, 55, 0.15)',
              border: '1px solid rgba(212, 175, 55, 0.3)',
              borderRadius: '24px',
              padding: '10px 18px',
              marginBottom: '24px',
              color: 'var(--accent-gold)',
              fontSize: '13px',
              fontWeight: 600
            }}>
              <Award size={16} />
              <span>{t('hero.badge')}</span>
            </div>

            {/* Main Headline */}
            <h1 className="hero-headline" style={{
              fontSize: 'clamp(36px, 5vw, 58px)',
              fontWeight: 800,
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              marginBottom: '20px',
              color: 'var(--text-primary)'
            }}>
              {t('hero.title')}
              <span style={{
                display: 'block',
                background: 'linear-gradient(135deg, var(--accent-gold) 0%, #F5D78E 50%, var(--accent-gold) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginTop: '4px'
              }}>
                {t('hero.titleHighlight')}
              </span>
            </h1>

            {/* Subheadline */}
            <p className="hero-subheadline" style={{
              fontSize: '16px',
              lineHeight: 1.7,
              color: 'var(--text-secondary)',
              maxWidth: '500px',
              marginBottom: '28px',
              fontWeight: 400
            }}>
              {t('hero.subtitle')}
            </p>

            {/* Key Benefits */}
            <div className="hero-benefits" style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '16px',
              marginBottom: '32px'
            }}>
              {[
                { icon: <Zap size={16} />, text: t('hero.instantFunding') },
                { icon: <Shield size={16} />, text: t('hero.profitSplit') },
                { icon: <TrendingUp size={16} />, text: t('hero.trading247') }
              ].map((benefit, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: 'var(--text-secondary)',
                  fontSize: '14px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  padding: '8px 14px',
                  borderRadius: '20px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <div style={{ color: 'var(--accent-gold)' }}>
                    {benefit.icon}
                  </div>
                  {benefit.text}
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hero-cta" style={{
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              <Button
                onClick={() => window.location.href = '#plans'}
                className="btn-primary"
                style={{
                  padding: '16px 32px',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: 600,
                  boxShadow: '0 4px 24px rgba(212, 175, 55, 0.3)'
                }}
              >
                {t('hero.getFundedNow')}
                <ArrowRight size={18} />
              </Button>
              <Button
                onClick={() => window.location.href = '#rules'}
                variant="outline"
                style={{
                  padding: '16px 28px',
                  fontSize: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.05)',
                  color: 'var(--text-primary)',
                  fontWeight: 500
                }}
              >
                {t('hero.viewRules')}
              </Button>
            </div>
          </div>

          {/* Stats Card - Right Side */}
          <div className="hero-stats-card" style={{
            background: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            padding: '32px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '28px'
            }}>
              <img src="/logo.png" alt="Prop Indexa" style={{ height: '45px' }} />
              <div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: 800,
                  color: 'var(--text-primary)',
                  letterSpacing: '0.05em'
                }}>
                  PROP INDEXA
                </div>
                <div style={{
                  fontSize: '12px',
                  color: 'var(--accent-gold)',
                  fontWeight: 500
                }}>
                  Premium Prop Trading
                </div>
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px'
            }}>
              {animatedStats.map((stat, index) => (
                <div key={index} style={{
                  padding: '18px',
                  background: 'rgba(212, 175, 55, 0.08)',
                  borderRadius: '14px',
                  border: '1px solid rgba(212, 175, 55, 0.15)'
                }}>
                  <div style={{
                    fontSize: '28px',
                    fontWeight: 700,
                    color: 'var(--accent-gold)',
                    marginBottom: '2px',
                    letterSpacing: '-0.02em'
                  }}>
                    {stat.prefix}
                    {stat.current.toFixed(stat.value < 100 ? 1 : 0)}
                    {stat.suffix}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: 'var(--text-muted)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Trust Badges */}
            <div style={{
              marginTop: '24px',
              paddingTop: '20px',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              justifyContent: 'center',
              gap: '20px',
              flexWrap: 'wrap'
            }}>
              {['Secure Payments', 'Fast Payouts', '24/7 Support'].map((badge, idx) => (
                <div key={idx} style={{
                  fontSize: '11px',
                  color: 'var(--text-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}>
                  <div style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: 'var(--success)'
                  }} />
                  {badge}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Responsive Styles */}
      <style>{`
        /* Desktop */
        .hero-grid {
          grid-template-columns: 1fr 1fr;
        }
        
        .hero-content {
          text-align: left;
        }
        
        /* Tablet */
        @media (max-width: 1024px) {
          .hero-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
          
          .hero-content {
            text-align: center;
            order: 1;
          }
          
          .hero-stats-card {
            order: 2;
            max-width: 450px;
            margin: 0 auto;
          }
          
          .hero-subheadline {
            margin-left: auto;
            margin-right: auto;
          }
          
          .hero-benefits {
            justify-content: center;
          }
          
          .hero-cta {
            justify-content: center;
          }
        }
        
        /* Mobile */
        @media (max-width: 640px) {
          .hero-section {
            padding-top: 70px !important;
          }
          
          .hero-headline {
            font-size: 32px !important;
          }
          
          .hero-subheadline {
            font-size: 15px !important;
          }
          
          .hero-benefits {
            gap: 10px !important;
          }
          
          .hero-benefits > div {
            font-size: 12px !important;
            padding: 6px 12px !important;
          }
          
          .hero-cta button {
            width: 100%;
            justify-content: center;
          }
          
          .hero-stats-card {
            padding: 24px !important;
          }
          
          .hero-stats-card > div:first-child img {
            height: 38px !important;
          }
          
          .hero-bg-image {
            opacity: 0.7 !important;
          }
          
          .hero-overlay {
            background: linear-gradient(180deg, rgba(11, 11, 11, 0.9) 0%, rgba(11, 11, 11, 0.7) 50%, rgba(11, 11, 11, 0.85) 100%) !important;
          }
        }
        
        /* Small Mobile */
        @media (max-width: 400px) {
          .hero-headline {
            font-size: 28px !important;
          }
          
          .hero-stats-card > div:last-child > div:first-child {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
};

export default Hero;
